import {
  createTranslator,
  type Locale,
  locales,
  negotiateLocale,
  type Translate,
} from '@itin/shared'
import { createContext, use, useMemo, useState, type ReactNode } from 'react'

/**
 * Every string a user reads comes through here.
 *
 * There is no "add English now, translate later" path in this codebase, because
 * that path never gets walked. `t` takes a typed key; a hardcoded sentence in a
 * component is a bug a reviewer can spot at a glance.
 */

interface LocaleContextValue {
  locale: Locale
  setLocale(locale: Locale): void
  t: Translate
}

const LocaleContext = createContext<LocaleContextValue | null>(null)

const STORAGE_KEY = 'itin.locale'

function initialLocale(): Locale {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored && (locales as readonly string[]).includes(stored)) return stored as Locale

  /**
   * A traveller's page opens in the language the itinerary is WRITTEN in, not
   * the language their browser happens to be set to.
   *
   * Negotiating from the browser produced the worst possible result: Ana writes
   * her days in Portuguese, so an English-language browser rendered Portuguese
   * content inside English furniture — "EMERGENCY" over "Consulado do Brasil",
   * "Confirmation" over a Portuguese note. Half-translated reads as broken.
   *
   * The advisor's own screens still negotiate, because there the interface IS
   * the content. Only the share route is pinned, and the toggle in its header
   * lets a reader disagree in one tap.
   */
  if (/^\/(s|intake)\//.test(window.location.pathname)) return 'pt-BR'
  return negotiateLocale(navigator.languages?.join(','), 'pt-BR')
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale)

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      setLocale(next) {
        localStorage.setItem(STORAGE_KEY, next)
        document.documentElement.lang = next
        setLocaleState(next)
      },
      t: createTranslator(locale),
    }),
    [locale],
  )

  return <LocaleContext value={value}>{children}</LocaleContext>
}

export function useLocale(): LocaleContextValue {
  const value = use(LocaleContext)
  if (!value) throw new Error('useLocale must be used inside <LocaleProvider>')
  return value
}

export function useT(): Translate {
  return useLocale().t
}

/** Formats a calendar date string without ever constructing a zoned Date. */
export function formatDate(isoDate: string, locale: Locale): string {
  const [year, month, day] = isoDate.split('-').map(Number) as [number, number, number]
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)))
}

/**
 * Formats an integer minor-units amount for display. This is the ONE place the
 * division by 100 happens, and it happens only here — at the last possible
 * moment, for a human to read. Every value that travels between client and
 * server, and every value held in state, stays an integer minor-units amount
 * plus its currency code; nothing upstream of this function ever divides.
 */
export function formatMoney(amountMinor: number, currency: string, locale: Locale): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amountMinor / 100)
}
